import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { api } from '../api';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { getToken } from '../auth';

const Page = styled.div`
  min-height: calc(100vh - 140px);
  display: flex;
  background: radial-gradient(circle at top left, #4f46e5 0, #312e81 35%, #020617 100%);
  padding: 1.5rem 1rem;
  gap: 1.25rem;
`;

const Sidebar = styled.aside`
  width: 240px;
  background: rgba(15, 23, 42, 0.96);
  color: #e5e7eb;
  border-radius: 16px;
  padding: 1.25rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const SidebarTitle = styled.div`
  font-weight: 800;
  font-size: 1rem;
  color: #f9fafb;
  margin-bottom: 0.75rem;
`;

const SideLink = styled.button`
  text-align: left;
  background: ${({ active }) => (active ? '#111827' : 'transparent')};
  border: none;
  color: ${({ active }) => (active ? '#ffffff' : '#cbd5e1')};
  padding: 0.6rem 0.8rem;
  border-radius: 999px;
  cursor: pointer;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  &:hover {
    background: #111827;
    color: #ffffff;
  }
`;

const Badge = styled.span`
  font-size: 0.7rem;
  padding: 0.1rem 0.5rem;
  border-radius: 999px;
  background: #1f2937;
`;

const Main = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #e5e7eb;
`;

const Title = styled.h1`
  font-size: 1.8rem;
  font-weight: 800;
  margin: 0;
`;

const SubTitle = styled.p`
  margin: 0.25rem 0 0;
  font-size: 0.9rem;
  color: #cbd5e1;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
`;

const StatCard = styled.div`
  background: rgba(15, 23, 42, 0.96);
  border-radius: 16px;
  padding: 1rem 1.1rem;
  border: 1px solid rgba(148, 163, 184, 0.25);
  color: #e5e7eb;
`;

const StatLabel = styled.div`
  font-size: 0.8rem;
  color: #9ca3af;
`;

const StatValue = styled.div`
  font-size: 1.6rem;
  font-weight: 800;
  margin-top: 0.2rem;
`;

const Surface = styled.div`
  background: #f9fafb;
  border-radius: 16px;
  padding: 1rem;
  border: 1px solid #e5e7eb;
`;

const SurfaceHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
`;

const SurfaceTitle = styled.h2`
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #111827;
`;

const Row = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
`;

const Input = styled.input`
  flex: 1;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  padding: 0.5rem 0.9rem;
  font-size: 0.9rem;
`;

const TableWrap = styled.div`
  overflow: auto;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
`;

const Th = styled.th`
  text-align: left;
  padding: 0.6rem 0.75rem;
  border-bottom: 1px solid #e5e7eb;
  background: #f3f4f6;
  position: sticky;
  top: 0;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 0.55rem 0.75rem;
  border-bottom: 1px solid #f3f4f6;
  white-space: nowrap;
`;

const Select = styled.select`
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  padding: 0.25rem 0.6rem;
  font-size: 0.85rem;
`;

const Button = styled.button`
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: white;
  border: none;
  padding: 0.45rem 0.9rem;
  border-radius: 999px;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
`;

const Tag = styled.span`
  border-radius: 999px;
  padding: 0.2rem 0.6rem;
  font-size: 0.75rem;
  font-weight: 600;
  ${({ variant }) =>
    variant === 'admin'
      ? 'background:#f97316; color:#111827;'
      : variant === 'premium'
      ? 'background:#4ade80; color:#022c22;'
      : 'background:#e5e7eb; color:#111827;'};
`;

const Muted = styled.span`
  color: #9ca3af;
  font-size: 0.8rem;
`;

const Admin = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [authorized, setAuthorized] = useState(false);
  const [activeTab, setActiveTab] = useState('users');
  const [blogPosts, setBlogPosts] = useState([]);
  const [premiumDays, setPremiumDays] = useState({});
  const [blogForm, setBlogForm] = useState({
    id: null,
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    tags: '',
    is_published: false
  });
  const navigate = useNavigate();

  const authHeader = () => {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const guard = async () => {
    const token = getToken();
    if (!token) {
      toast.error('Admin paneline erişmek için önce giriş yapın.');
      navigate('/login');
      return;
    }

    try {
      const { data } = await api.get('/api/auth/me', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if ((data.role || 'user') === 'admin') {
        setAuthorized(true);
      } else {
        toast.error('Bu sayfaya erişmek için admin yetkisi gerekli.');
        navigate('/');
      }
    } catch (e) {
      toast.error('Oturum süreniz geçerli değil, lütfen tekrar giriş yapın.');
      navigate('/login');
    }
  };

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/api/admin/users-db', { headers: authHeader() });
      setUsers(data.users || []);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const updateUser = async (email, next) => {
    try {
      await api.post('/api/admin/users-db/update', next, { headers: authHeader() });
      toast.success('Güncellendi');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Güncellenemedi');
    }
  };

  const loadBlogPosts = async () => {
    try {
      const { data } = await api.get('/api/admin/blog/posts', { headers: authHeader() });
      setBlogPosts(data.posts || []);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Blog yazıları yüklenemedi');
    }
  };


  const updatePremiumDays = async (email) => {
    const raw = premiumDays[email];
    const days = Number(raw);
    if (!days || days <= 0) {
      toast.error('Gecerli gun sayisi girin');
      return;
    }
    await updateUser(email, { email, premium_days: days });
  };


  const resetBlogForm = () => {
    setBlogForm({
      id: null,
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      tags: '',
      is_published: false
    });
  };

  const editBlogPost = (post) => {
    setActiveTab('blog');
    setBlogForm({
      id: post.id,
      title: post.title || '',
      slug: post.slug || '',
      excerpt: post.excerpt || '',
      content: post.content || '',
      tags: (post.tags || []).join(', '),
      is_published: !!post.is_published
    });
  };

  const submitBlogForm = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        title: blogForm.title,
        slug: blogForm.slug,
        excerpt: blogForm.excerpt,
        content: blogForm.content,
        tags: blogForm.tags,
        is_published: blogForm.is_published
      };

      if (blogForm.id) {
        await api.post(`/api/admin/blog/posts/${blogForm.id}`, payload, {
          headers: authHeader()
        });
        toast.success('Blog yazısı güncellendi');
      } else {
        await api.post('/api/admin/blog/posts', payload, {
          headers: authHeader()
        });
        toast.success('Blog yazısı oluşturuldu');
      }

      resetBlogForm();
      loadBlogPosts();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Blog yazısı kaydedilemedi');
    }
  };

  useEffect(() => {
    guard().then(() => {
      /* authorized state set in guard */
    });
  }, []);

  useEffect(() => {
    if (authorized) {
      load();
      loadBlogPosts();
    }
  }, [authorized]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return users;
    return users.filter(
      (u) =>
        (u.email || '').toLowerCase().includes(term) ||
        (u.name || '').toLowerCase().includes(term)
    );
  }, [users, q]);

  const metrics = useMemo(() => {
    const total = users.length;
    const premium = users.filter((u) => u.is_premium || u.plan === 'premium').length;
    const admin = users.filter((u) => (u.role || 'user') === 'admin').length;
    const active = users.filter((u) => u.is_active !== false).length;
    return { total, premium, admin, active };
  }, [users]);

  if (!authorized) return null;

  return (
    <Page>
      <Sidebar>
        <SidebarTitle>İşHazır Admin</SidebarTitle>
        <SideLink
          type="button"
          active={activeTab === 'overview'}
          onClick={() => setActiveTab('overview')}
        >
          Genel Bakış
        </SideLink>
        <SideLink
          type="button"
          active={activeTab === 'users'}
          onClick={() => setActiveTab('users')}
        >
          Kullanıcılar
          <Badge>{metrics.total}</Badge>
        </SideLink>
        <SideLink
          type="button"
          active={activeTab === 'blog'}
          onClick={() => setActiveTab('blog')}
        >
          Blog Yazıları
          <Badge>{blogPosts.length}</Badge>
        </SideLink>
        <SideLink type="button" disabled style={{ opacity: 0.4, cursor: 'not-allowed' }}>
          Ödemeler
          <Badge>yakında</Badge>
        </SideLink>
        <SideLink type="button" disabled style={{ opacity: 0.4, cursor: 'not-allowed' }}>
          Ayarlar
          <Badge>yakında</Badge>
        </SideLink>
      </Sidebar>

      <Main>
        <Header>
          <div>
            <Title>Admin Paneli</Title>
            <SubTitle>Sistemdeki tüm kullanıcıları ve planları buradan yönet.</SubTitle>
          </div>
        </Header>

        <StatsGrid>
          <StatCard>
            <StatLabel>Toplam Kullanıcı</StatLabel>
            <StatValue>{metrics.total}</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>Aktif Kullanıcı</StatLabel>
            <StatValue>{metrics.active}</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>Premium</StatLabel>
            <StatValue>{metrics.premium}</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>Admin</StatLabel>
            <StatValue>{metrics.admin}</StatValue>
          </StatCard>
        </StatsGrid>

        {activeTab === 'overview' && (
          <Surface>
            <SurfaceHeader>
              <SurfaceTitle>Son Kullanıcılar</SurfaceTitle>
              <Muted>En son kayıt olan ilk 5 kullanıcı listelenir.</Muted>
            </SurfaceHeader>
            {users.length === 0 ? (
              <Muted>Henüz kayıtlı kullanıcı bulunmuyor.</Muted>
            ) : (
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <Th>Email</Th>
                      <Th>İsim</Th>
                      <Th>Plan</Th>
                      <Th>Rol</Th>
                      <Th>Kayıt Tarihi</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.slice(0, 5).map((u) => (
                      <tr key={u.id}>
                        <Td>{u.email}</Td>
                        <Td>{u.name || u.email.split('@')[0]}</Td>
                        <Td>
                          <Tag
                            variant={
                              u.is_premium || u.plan === 'premium' ? 'premium' : 'default'
                            }
                          >
                            {u.is_premium || u.plan === 'premium' ? 'premium' : 'basic'}
                          </Tag>
                        </Td>
                        <Td>
                          <Tag variant={u.role === 'admin' ? 'admin' : 'default'}>
                            {u.role || 'user'}
                          </Tag>
                        </Td>
                        <Td>
                          {u.created_at
                            ? new Date(u.created_at).toLocaleDateString('tr-TR')
                            : '-'}
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
            )}
          </Surface>
        )}

        {activeTab === 'users' && (
          <Surface>
            <SurfaceHeader>
              <SurfaceTitle>Kullanıcılar</SurfaceTitle>
              <Button type="button" onClick={load} disabled={loading}>
                {loading ? 'Yükleniyor...' : 'Yenile'}
              </Button>
            </SurfaceHeader>
            <Row style={{ marginBottom: '0.75rem' }}>
              <Input
                placeholder="Kullanıcı ara (email, isim)"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
              <Muted>{filtered.length} sonuç</Muted>
            </Row>
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <Th>Email</Th>
                    <Th>İsim</Th>
                    <Th>Plan</Th>
                    <Th>Rol</Th>
                    <Th>Durum</Th>
                    <Th>Premium Bitiş</Th>
                    <Th>Aksiyon</Th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((u) => (
                    <tr key={u.id}>
                      <Td>{u.email}</Td>
                      <Td>{u.name || u.email.split('@')[0]}</Td>
                      <Td>
                        <Select
                          defaultValue={u.plan}
                          onChange={(e) =>
                            updateUser(u.email, { email: u.email, plan: e.target.value })
                          }
                        >
                          <option value="basic">basic</option>
                          <option value="premium">premium</option>
                        </Select>
                      </Td>
                      <Td>
                        <Select
                          defaultValue={u.role}
                          onChange={(e) =>
                            updateUser(u.email, { email: u.email, role: e.target.value })
                          }
                        >
                          <option value="user">user</option>
                          <option value="admin">admin</option>
                        </Select>
                      </Td>
                      <Td>
                        {u.is_active === false ? (
                          <Tag>pasif</Tag>
                        ) : (
                          <Tag variant="premium">aktif</Tag>
                        )}
                      </Td>
                      <Td>
                        {u.premium_until
                          ? new Date(u.premium_until).toLocaleDateString('tr-TR')
                          : '-'}
                      </Td>
                      <Td>
                        <Button
                          type="button"
                          onClick={() =>
                            updateUser(u.email, {
                              email: u.email,
                              plan: u.plan,
                              role: u.role
                            })
                          }
                        >
                          Kaydet
                        </Button>
                        <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                          <input
                            style={{
                              width: 90,
                              border: '1px solid #e5e7eb',
                              borderRadius: 999,
                              padding: '0.25rem 0.5rem',
                              fontSize: '0.8rem'
                            }}
                            placeholder="Gun"
                            value={premiumDays[u.email] || ''}
                            onChange={(e) =>
                              setPremiumDays((prev) => ({
                                ...prev,
                                [u.email]: e.target.value
                              }))
                            }
                          />
                          <Button type="button" onClick={() => updatePremiumDays(u.email)}>
                            Premium Ver
                          </Button>
                        </div>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
            {loading && <Muted>Yükleniyor...</Muted>}
          </Surface>
        )}

        {activeTab === 'blog' && (
          <Surface>
            <SurfaceHeader>
              <SurfaceTitle>Blog Yazıları</SurfaceTitle>
              <Button type="button" onClick={resetBlogForm}>
                Yeni Yazı
              </Button>
            </SurfaceHeader>

            <form onSubmit={submitBlogForm} style={{ marginBottom: '1rem' }}>
              <Row style={{ marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                <Input
                  style={{ flex: 1, minWidth: 220 }}
                  placeholder="Başlık"
                  value={blogForm.title}
                  onChange={(e) =>
                    setBlogForm((f) => ({ ...f, title: e.target.value }))
                  }
                />
                <Input
                  style={{ flex: 1, minWidth: 220 }}
                  placeholder="Slug (seo-url)"
                  value={blogForm.slug}
                  onChange={(e) =>
                    setBlogForm((f) => ({ ...f, slug: e.target.value }))
                  }
                />
              </Row>
              <Row style={{ marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                <Input
                  style={{ flex: 1 }}
                  placeholder="Kısa özet (listelemelerde görünecek)"
                  value={blogForm.excerpt}
                  onChange={(e) =>
                    setBlogForm((f) => ({ ...f, excerpt: e.target.value }))
                  }
                />
              </Row>
              <Row style={{ marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                <Input
                  as="textarea"
                  style={{ flex: 1, minHeight: 120, resize: 'vertical' }}
                  placeholder="İçerik (markdown veya düz metin)"
                  value={blogForm.content}
                  onChange={(e) =>
                    setBlogForm((f) => ({ ...f, content: e.target.value }))
                  }
                />
              </Row>
              <Row style={{ alignItems: 'center', gap: '0.5rem' }}>
                <Input
                  style={{ flex: 1 }}
                  placeholder="Etiketler (virgülle ayırın)"
                  value={blogForm.tags}
                  onChange={(e) =>
                    setBlogForm((f) => ({ ...f, tags: e.target.value }))
                  }
                />
                <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <input
                    type="checkbox"
                    checked={blogForm.is_published}
                    onChange={(e) =>
                      setBlogForm((f) => ({
                        ...f,
                        is_published: e.target.checked
                      }))
                    }
                  />
                  Yayınla
                </label>
                <Button type="submit">
                  {blogForm.id ? 'Güncelle' : 'Oluştur'}
                </Button>
              </Row>
            </form>

            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <Th>Başlık</Th>
                    <Th>Slug</Th>
                    <Th>Durum</Th>
                    <Th>Yayın Tarihi</Th>
                    <Th>Aksiyon</Th>
                  </tr>
                </thead>
                <tbody>
                  {blogPosts.map((post) => (
                    <tr key={post.id}>
                      <Td>{post.title}</Td>
                      <Td>{post.slug}</Td>
                      <Td>
                        {post.is_published ? (
                          <Tag variant="premium">yayında</Tag>
                        ) : (
                          <Tag>taslak</Tag>
                        )}
                      </Td>
                      <Td>
                        {post.published_at
                          ? new Date(post.published_at).toLocaleDateString('tr-TR')
                          : '-'}
                      </Td>
                      <Td>
                        <Button type="button" onClick={() => editBlogPost(post)}>
                          Düzenle
                        </Button>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          </Surface>
        )}
      </Main>
    </Page>
  );
};

export default Admin;
